/**
 * PhaseTabs — WEAVE / RUN / ATTEST / RECALL.
 *
 * Cycle-102 sprint-2 Task 2.4 (sprint plan §353-359).
 *
 * Visual scaffold only — NO ceremonies, NO composition flow. Per
 * PRD F-2.4, sprint-2 ships URL-state binding + ARIA tablist nav and
 * the per-phase visual filter; phase semantics defer to cycle-102.5
 * sprint-2.
 *
 * URL state: `?phase=WEAVE|RUN|ATTEST|RECALL` (default WEAVE).
 *
 * View-mode preservation per SDD R-SDD-9 + Open Q-6:
 *   - The legacy 6 view modes map onto the 4 phases:
 *     build → WEAVE, monitor → RUN, runs → ATTEST, memory → RECALL.
 *   - `lineage` and `review` survive as URL-only sub-modes inside
 *     RECALL (`?phase=RECALL&sub=lineage|review`); see
 *     `phaseMapping.ts::PHASE_FROM_VIEW_MODE`.
 *
 * ARIA pattern: tablist with arrow-key + Home/End nav, mirroring
 * `frontend/src/components/workspace/ModesRow.tsx` (cycle-101 sprint-2
 * precedent).
 */

import { useCallback, useRef } from 'react';

import { cn } from '../../../lib/utils';

export const PHASE_IDS = ['WEAVE', 'RUN', 'ATTEST', 'RECALL'] as const;
export type PhaseId = (typeof PHASE_IDS)[number];

export const DEFAULT_PHASE: PhaseId = 'WEAVE';

/**
 * Per-phase one-line description shown in the tab title attribute and
 * available to screen readers via aria-describedby.
 */
const PHASE_DESCRIPTION: Record<PhaseId, string> = {
  WEAVE: 'Compose investigations, forge patterns, theatres',
  RUN: 'Active runs and execution traces',
  ATTEST: 'Attestations, certificates, audit trail',
  RECALL: 'Memory, lineage, prior reviews',
};

/**
 * Per-phase glyph (◧▶✦◆) shown alongside the phase name. Decorative —
 * marked aria-hidden so the accessible name remains the phase text.
 * Cycle-105.8 sprint-2 Task 230.1 (FR-E-2).
 */
const PHASE_GLYPH: Record<PhaseId, string> = {
  WEAVE: '◧',
  RUN: '▶',
  ATTEST: '✦',
  RECALL: '◆',
};

interface PhaseTabsProps {
  activePhase: PhaseId;
  onPhaseChange: (phase: PhaseId) => void;
  /** id of the canvas element this tablist controls — aria-controls target. */
  canvasId?: string;
  className?: string;
}

export function PhaseTabs({
  activePhase,
  onPhaseChange,
  canvasId,
  className,
}: PhaseTabsProps) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleKey = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      const count = PHASE_IDS.length;
      let nextIndex: number | null = null;
      if (e.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % count;
      } else if (e.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + count) % count;
      } else if (e.key === 'Home') {
        nextIndex = 0;
      } else if (e.key === 'End') {
        nextIndex = count - 1;
      }
      if (nextIndex !== null) {
        e.preventDefault();
        const nextPhase = PHASE_IDS[nextIndex];
        onPhaseChange(nextPhase);
        tabRefs.current[nextIndex]?.focus();
      }
    },
    [onPhaseChange],
  );

  return (
    <div
      role="tablist"
      aria-label="Environment phase"
      aria-orientation="horizontal"
      className={cn(
        'flex items-center gap-1 px-3 h-9 border-b border-[var(--border-subtle)] bg-[var(--bg-panel)]',
        className,
      )}
    >
      {PHASE_IDS.map((phase, i) => {
        const isActive = phase === activePhase;
        return (
          <button
            key={phase}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            role="tab"
            id={`phase-tab-${phase}`}
            aria-selected={isActive}
            aria-controls={canvasId}
            tabIndex={isActive ? 0 : -1}
            onKeyDown={(e) => handleKey(e, i)}
            onClick={() => onPhaseChange(phase)}
            title={PHASE_DESCRIPTION[phase]}
            data-testid={`phase-tab-${phase}`}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest rounded-sm border transition-colors',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sig-iris)]',
              isActive
                ? 'border-[var(--sig-iris)] text-[var(--fg)] bg-[var(--sig-iris)]/10'
                : 'border-[var(--border-subtle)] text-[var(--fg-muted)] hover:text-[var(--fg)] hover:bg-[var(--bg-hover)]',
            )}
          >
            <span aria-hidden="true" className="text-[11px] leading-none">
              {PHASE_GLYPH[phase]}
            </span>
            {phase}
          </button>
        );
      })}
    </div>
  );
}
